import {parse, format, resolve as resolve} from 'url';
import routerInstance from 'router';
import {Router, RouterActionOptions} from '../router/';
import {NON_ADAPTIVE_PAGE_REGEXP} from '../constants';
import {Locale} from 'contexts/LocaleContext';
import {Env} from 'contexts/EnvContext';
import {getCurrencyFormatter} from '../../common/i18n/utils';
import {Query} from 'models/api';

export function getURL(href: string = window.location.href) {
    return href.startsWith(window.location.origin)
        ? href.slice(window.location.origin.length)
        : href;
}

export function getServiceLink(
    service: string,
    tld = 'ru',
    env = Env.Production,
    isExternal = false,
) {
    const subdomain = env === Env.Testing ? `${service}-preprod` : service;
    const cloudDomain = isExternal ? '' : '.cloud';
    return `https://${subdomain}${cloudDomain}.yandex.${tld}`;
}

export function getConsoleLink(tld = 'ru', env = Env.Production) {
    return getServiceLink('console', tld, env);
}

export function getDatalensLink(tld = 'ru', env = Env.Production) {
    return getServiceLink('datalens', tld, env, true);
}

export function getConsoleSpecialLink(
    tld = 'ru',
    env = Env.Production,
    url: string,
    params?: Record<string, string>,
) {
    const domain = getConsoleLink(tld, env);
    const urlParams = new URLSearchParams(params);
    return `${domain}/link${url}?${urlParams.toString()}`;
}

export function setUrlTld(url: string, tld = 'ru') {
    return typeof url === 'string' ? url?.replace(/\${tld}/g, tld) : url;
}

export function isModifiedEvent(event: MouseEvent) {
    return Boolean(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

export function isLeftMouseButtonClick(event: MouseEvent) {
    return typeof event.button === 'number' ? event.button === 0 : event.which === 1;
}

export function isLocalLink(href: string) {
    try {
        const link = document.createElement('a');
        link.href = href;
        return link.origin === window.location.origin;
    } catch (_) {
        return false;
    }
}

export function getFullAnchorUrl(anchor: string | null) {
    const query = window.location.search || '';

    return anchor && resolve(String(window.location.pathname) + query, anchor);
}

const bytesExponentByUnit: Record<string, number> = {
    b: 0,
    kb: 10,
    mb: 20,
    gb: 30,
    tb: 40,
    pb: 50,
    eb: 60,
};

function getBytesExponent(unit: string) {
    if (!bytesExponentByUnit[unit]) {
        throw new Error(`Unknown unit: ${unit}`);
    }
    return bytesExponentByUnit[unit];
}

export function toBytes(value: number, unit: string) {
    const exponent = getBytesExponent(unit);
    return value * Math.pow(2, exponent);
}

export function fromBytes(value: number, unit: string) {
    const exponent = getBytesExponent(unit);
    return value / Math.pow(2, exponent);
}

export function parseBytes(value: string, unit: string) {
    return toBytes(parseStringValue(value), unit);
}

export function parseStringValue(value: string) {
    const numbers = value.match(/[+-]?([0-9]*[.])?[0-9]+/g);
    if (numbers && numbers.length > 0) {
        return Number(numbers[0]);
    }
    return 0;
}

const secondsMultiplierByUnit: Record<string, number> = {
    second: 1,
    minute: 60,
    hour: 60 * 60,
};

function getSecondsMultiplier(unit: string) {
    if (!secondsMultiplierByUnit[unit]) {
        throw new Error(`Unknown unit: ${unit}`);
    }
    return secondsMultiplierByUnit[unit];
}

export function toSeconds(value: number, unit: string) {
    const multiplier = getSecondsMultiplier(unit);
    return value * multiplier;
}

export function fromSeconds(value: number, unit: string) {
    const multiplier = getSecondsMultiplier(unit);
    return value / multiplier;
}

export function roundNumber(value: number, minPrecision = 2, maxPrecision = minPrecision): number {
    if (value === 0) {
        return 0;
    }

    const originalNumber = Number(value);
    const roundedNumber =
        Math.round(originalNumber * Math.pow(10, minPrecision)) / Math.pow(10, minPrecision);

    return roundedNumber === 0 && minPrecision < maxPrecision
        ? roundNumber(originalNumber, minPrecision + 1, maxPrecision)
        : roundedNumber;
}

export function formatNumber(value: number) {
    const str = String(value);
    const [intStr, fracStr] = str.split('.');
    let i = intStr.length - 1;
    let acc = 0;

    let resultStr = '';

    if (intStr.length > 3) {
        while (i >= 0) {
            if (acc === 3) {
                resultStr = '\u00a0' + resultStr;
                acc = 0;
            }

            resultStr = intStr[i] + resultStr;
            acc++;
            i--;
        }
    } else {
        resultStr = intStr;
    }

    if (fracStr) {
        resultStr += `.${fracStr}`;

        if (fracStr.length === 1) {
            resultStr += '0';
        }
    }

    return resultStr || '0';
}

export function parseNumber(str: string, precision = 2, defaultValue = 0) {
    const value = roundNumber(parseFloat(str.replace(/\u00a0/g, '')), precision);
    return isNaN(value) ? defaultValue : value;
}

interface PriceFormatConfig {
    minPrecision?: number;
    maxPrecision?: number;
    isInteger?: boolean;
}

export function formatPrice(
    value: number,
    {code}: Locale,
    {minPrecision, maxPrecision, isInteger}: PriceFormatConfig = {},
) {
    const intlFormat = getCurrencyFormatter(code, isInteger);
    return intlFormat.format(roundNumber(value, minPrecision, maxPrecision));
}

export function formatName(name: string) {
    return name.replace(' — ', '\u00a0—\u00a0');
}

export function getRandomInt(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const findClosest = (arr: number[], value: number, preferLeftValue = true) => {
    if (!arr.length) {
        return null;
    }

    let lowIndex = 0;
    let hiIndex = arr.length - 1;

    while (hiIndex - lowIndex > 1) {
        const mid = Math.floor((lowIndex + hiIndex) / 2);
        if (arr[mid] < value) {
            lowIndex = mid;
        } else {
            hiIndex = mid;
        }
    }

    const leftValue = arr[lowIndex];
    const rightValue = arr[hiIndex];

    if (value - leftValue <= rightValue - value && preferLeftValue) {
        return leftValue;
    } else {
        return rightValue;
    }
};

export function getValueInRange(range: {min: number; max: number}, value: number) {
    const {min, max} = range;

    if (value < min) {
        return min;
    }

    if (value > max) {
        return max;
    }
    return value;
}

export function toDataAttrs(obj: Record<string, unknown>) {
    return obj
        ? Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
              acc[`data-${key}`] = obj[key];
              return acc;
          }, {})
        : null;
}

export interface ScriptOptions {
    id: string;
    defer?: boolean;
    async?: boolean;
}

export async function loadScript(src: string, {id, defer = false, async = true}: ScriptOptions) {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.id = id;
        script.defer = defer;
        script.async = async;
        script.onload = (event) => {
            resolve(event);
        };
        script.onerror = (event) => {
            const element = document.getElementById(id);
            element?.parentNode?.removeChild(element);
            reject(event);
        };
        document.head.appendChild(script);
    });
}

export async function copyToClipboard(text: string) {
    if (!text) {
        return;
    }

    if (navigator.clipboard && typeof navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    // fallback for old browsers
    const textarea = document.createElement('textarea');
    textarea.setAttribute('style', 'position: absolute; left: 1000%');
    textarea.textContent = text;
    document.body.append(textarea);

    textarea.select();
    document.execCommand('copy');

    document.body.removeChild(textarea);
}

export function getParentUrl(pathname: string) {
    if (!pathname) {
        return null;
    }

    const pathParts = pathname.split('/');
    pathParts.pop();
    return pathParts.join('/');
}

export function checkIsAuthorized(status: string) {
    return status === 'VALID';
}

export function getAuthLink(passportHost: string, retPath: string) {
    return `${passportHost}/auth?retpath=${retPath}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function combineUseReducers(combinedReducers: Record<string, any[]>) {
    const state = Object.keys(combinedReducers).reduce(
        (acc, key) => ({...acc, [key]: combinedReducers[key][0]}),
        {},
    );

    const dispatch = (action: unknown) =>
        Object.keys(combinedReducers)
            .map((key) => combinedReducers[key][1])
            .forEach((fn) => fn(action));

    return [state, dispatch];
}

export interface QueryParam {
    name: string;
    value?: string | number | null;
}

export async function setQueryParams(
    params: QueryParam | QueryParam[],
    hash?: string,
    options?: RouterActionOptions,
): Promise<void>;
export async function setQueryParams(
    params: QueryParam | QueryParam[],
    upScroll?: Boolean,
    options?: RouterActionOptions,
): Promise<void>;
export async function setQueryParams(
    params: QueryParam | QueryParam[],
    upScrollHash: Boolean | string = true,
    options?: RouterActionOptions,
) {
    const queryParams = new URLSearchParams(location.search);
    const newParams = Array.isArray(params) ? params : [params];

    newParams.forEach(({name, value}) => {
        if (value) {
            queryParams.set(name, value.toString());
        } else {
            queryParams.delete(name);
        }
    });

    const url = `${location.pathname}?${queryParams}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (routerInstance as any).push(url, url, options);

    if (upScrollHash) {
        if (typeof upScrollHash === 'string') {
            scrollToHash(upScrollHash);
        } else {
            window.scrollTo(0, 0);
        }
    }
}

export function checkPageAdaptive(pathname: string) {
    if (!pathname) {
        return false;
    }

    return !NON_ADAPTIVE_PAGE_REGEXP.some((nonAdaptiveRegExp) => nonAdaptiveRegExp.test(pathname));
}

export function toggleAdaptiveClass(pathname: string) {
    document.documentElement.classList.toggle('adaptive', checkPageAdaptive(pathname));
}

export function getAbsolutePath(router: Router, url?: string) {
    if (!router || !router.pathname) {
        return url ?? '';
    }

    const parsed = parse(url || router.as || '');

    return format({
        ...parsed,
        protocol: parsed.protocol || 'https',
        hostname: parsed.hostname || router.hostname,
        pathname: parsed.pathname || router.pathname,
    });
}

export function getNonLocaleHostName(hostname: string) {
    return hostname.replace(/\.(ru|com)$/, '');
}

export function isLinkExternal(url: string, router: Router) {
    if (!router || !router.hostname) {
        return true;
    }

    const {hostname} = parse(url);

    if (!hostname) {
        return false;
    }

    return getNonLocaleHostName(hostname) !== getNonLocaleHostName(router.hostname);
}

export function getLinkProps(url: string, router: Router) {
    return isLinkExternal(url, router) ? {target: '_blank', rel: 'noopener noreferrer'} : {};
}

export function countWords(str: string) {
    return str.split(' ').filter(Boolean).length;
}

export function removeHash(router?: Router) {
    if (router) {
        const [url] = router.as.split('#');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (routerInstance as any).replace(url);
    } else if (history && 'replaceState' in history) {
        history.replaceState('', document.title, window.location.pathname + window.location.search);
    } else {
        window.location.hash = '';
    }
}

export function scrollToHash(hash: string, browser?: string) {
    if (!hash) {
        return;
    }

    const element = document.getElementById(hash);
    if (!element) {
        return;
    }

    setTimeout(
        () => element.scrollIntoView({behavior: browser === 'Yandex' ? 'auto' : 'smooth'}),
        0,
    );
}

// https://developer.mozilla.org/ru/docs/Glossary/Base64#solution_1_–_escaping_the_string_before_encoding_it
export function base64DecodeUnicode(str: string) {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(
        atob(str)
            .split('')
            .map((c) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(''),
    );
}

export type InnerProps<TProps extends Partial<TDefaultProps>, TDefaultProps> = Omit<
    TProps,
    keyof TDefaultProps
> &
    Required<Pick<TProps, keyof TDefaultProps>>;

export function isRootPage(pathname: string) {
    return pathname === '/';
}

export const getPageSearchParams = (query: Query = {}) => {
    const searchParams = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
        searchParams.set(key, String(value));
    });

    return searchParams;
};
